import { useCMSPageField } from '@/cms/fields';
import { Typography, tk, Button } from '@sunwebgroup/ui-components';
import styled from 'styled-components';
import type { BreadcrumbItem } from '@/cms/fields/types/common';

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
}

const BreadcrumbContainer = styled.ol`
  line-height: inherit;
  flex-wrap: wrap;
  display: flex;
  gap: ${tk('spacing_xs')};
  list-style: none;
  margin-left: ${tk('spacing_none')};
  padding-inline-start: ${tk('spacing_none')};
  margin-top: ${tk('spacing_xs')};

  @media ${tk('mq_tablet')} {
    margin-bottom: ${tk('spacing_none')};
  }
`;

const BreadcrumbElement = styled.li`
  color: ${tk('color_palette_grey_500')};
  display: flex;
  align-items: center;

  &:last-child a {
    color: ${tk('color_palette_grey_400')};
    cursor: default;
  }

  &:not(:last-child)::after {
    content: ',';
  }
`;

const BreadcrumbLink = styled(Button)`
  padding: ${tk('spacing_none')} !important;
  color: ${tk('color_palette_grey_500')};
  min-height: 0px;
  min-width: 0;
  justify-content: normal;

  &:hover {
    color: ${tk('color_palette_primary_700')};
    text-decoration: none;
  }
`;

const BreadcrumbLinkTypography = styled(Typography)`
  && {
    // Override the default button typography -- Styled component known issue
    ${Typography.modVariant('body-sm')}!important;
  }
`;

export default function Breadcrumb({ items }: BreadcrumbProps) {
  const data = useCMSPageField('Breadcrumb.elements');
  let elements: BreadcrumbItem[] = [];

  if (!items && !data) {
    return;
  }

  if (items) {
    elements = items;
  }

  if (!items && data) {
    elements = data.map((item) => ({
      text: item.text,
      href: item.href,
    }));
  }

  return (
    <BreadcrumbContainer itemScope itemType="http://schema.org/BreadcrumbList" aria-label="Breadcrumb">
      {elements.map((item, index) => (
        <BreadcrumbElement itemProp="itemListElement" itemScope itemType="http://schema.org/ListItem" key={index}>
          <BreadcrumbLink itemProp="item" href={item.href} $withoutColor $hierarchy="tertiary" as="a">
            {elements.length - 1 === index ? (
              <BreadcrumbLinkTypography $variant="body-sm" as="span" itemProp="name" aria-current="page">
                {item.text}
              </BreadcrumbLinkTypography>
            ) : (
              <BreadcrumbLinkTypography $variant="body-sm" as="span" itemProp="name">
                {item.text}
              </BreadcrumbLinkTypography>
            )}
          </BreadcrumbLink>
          <meta itemProp="position" content={`${index + 1}`} />
        </BreadcrumbElement>
      ))}
    </BreadcrumbContainer>
  );
}
